import { Tooltip, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Tips } from '../../icons';
import droplet from '../../assets/droplet.png';
import type { memberInfoProps } from '../../types/member.type';
import { formatYmdFromDate, formatYmdFromTimestamp } from '../../util/date';
import AnimatedNumber from '../../util/AnimatedNumber';

export default function DropWaterDetailSidebar(props: { memberInfo: memberInfoProps | null }) {
  const { memberInfo } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:768px)');

  const total =
    (memberInfo?.flowingWater || 0) +
    (memberInfo?.memberDrops || 0) +
    (memberInfo?.supplyDrop || 0) +
    (memberInfo?.otherDrops || 0);
  const currentExpireDate = memberInfo?.currentMemberEndTime
    ? (() => {
        const date = new Date(memberInfo.currentMemberEndTime);
        const day = date.getDate();
        const totalMonths = date.getFullYear() * 12 + date.getMonth() + (memberInfo.renewalQueueMonths || 0);
        const targetYear = Math.floor(totalMonths / 12);
        const targetMonth = totalMonths % 12;
        const maxDay = new Date(targetYear, targetMonth + 1, 0).getDate();
        return formatYmdFromDate(new Date(targetYear, targetMonth, Math.min(day, maxDay)));
      })()
    : '';
  const supplyDropsExpiresAt = formatYmdFromTimestamp(
    memberInfo?.supplyDropsExpiresAtTimeStamp,
    memberInfo?.supplyDropsExpiresAt
  );

  if (isMobile) {
    return (
      <div className="dropWaterDetailSidebarMobile">
        <div className="dropWaterDetailTotalHeaderMobile">
          <span className="dropWaterDetailTotalLabelMobile">总水滴余额</span>
          <div className="dropWaterDetailTotalValueMobile">
            <img width={16} src={droplet} alt="droplet" />
            <AnimatedNumber value={total} />
          </div>
        </div>
        <div className="dropWaterDetailCardRow">
          <div className="dropWaterDetailCardMobile">
            <div className="dropWaterDetailCardTitleMobile">
              <span className="dropWaterDetailCardLabelMobile">免费水滴</span>
              <img width={10} src={droplet} alt="" />
              <span className="dropWaterDetailCardValueMobile"><AnimatedNumber value={memberInfo?.flowingWater || 0} /></span>
              <span className="dropWaterDetailCardSlashMobile">/200</span>
              <Tooltip title="最多可攒200滴，消耗后可继续领取" placement="top" enterTouchDelay={0}>
                <span className="dropWaterDetailCardTipMobile"><Tips /></span>
              </Tooltip>
            </div>
            <div className="dropWaterDetailCardSubMobile">登录可领 50 永久水滴</div>
          </div>
          <div className="dropWaterDetailCardMobile">
            <div className="dropWaterDetailCardTitleMobile">
              <span className="dropWaterDetailCardLabelMobile">会员水滴</span>
              <img width={10} src={droplet} alt="" />
              <span className="dropWaterDetailCardValueMobile"><AnimatedNumber value={memberInfo?.memberDrops || 0} /></span>
              <Tooltip title="会员有效期内可用，升级获得更多水滴" placement="top" enterTouchDelay={0}>
                <span className="dropWaterDetailCardTipMobile"><Tips /></span>
              </Tooltip>
            </div>
            <div className="dropWaterDetailCardSubMobile">
              {currentExpireDate ? t('expiration_date') + currentExpireDate : t('upgrade_to_get_more_drops')}
            </div>
          </div>
        </div>
        <div className="dropWaterDetailCardRow">
          <div className="dropWaterDetailCardMobile">
            <div className="dropWaterDetailCardTitleMobile">
              <span className="dropWaterDetailCardLabelMobile">补给水滴</span>
              <img width={10} src={droplet} alt="" />
              <span className="dropWaterDetailCardValueMobile"><AnimatedNumber value={memberInfo?.supplyDrop || 0} /></span>
              <Tooltip title="100天有效，每次获得新的补给水滴，剩余水滴有效期重置为100天" placement="top" enterTouchDelay={0}>
                <span className="dropWaterDetailCardTipMobile"><Tips /></span>
              </Tooltip>
            </div>
            <div className="dropWaterDetailCardSubMobile">
              {(supplyDropsExpiresAt && memberInfo?.supplyDrop) ? t('expiration_date') + supplyDropsExpiresAt : ''}
            </div>
          </div>
          <div className="dropWaterDetailCardMobile" style={{ visibility: memberInfo?.otherDrops ? 'visible' : 'hidden' }}>
            <div className="dropWaterDetailCardTitleMobile">
              <span className="dropWaterDetailCardLabelMobile">其他水滴</span>
              <img width={10} src={droplet} alt="" />
              <span className="dropWaterDetailCardValueMobile"><AnimatedNumber value={memberInfo?.otherDrops || 0} /></span>
            </div>
            <div className="dropWaterDetailCardSubMobile">&nbsp;</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dropWaterDetailSidebar">
      <div className="dropWaterDetailTotalHeader">
        <div className="dropWaterDetailTotalLabel">总水滴余额</div>
        <div className="dropWaterDetailTotalValue">
          <img width={20} src={droplet} alt="droplet" />
          <AnimatedNumber value={total} />
        </div>
      </div>
      <div className="dropWaterDetailCard">
        <div className="dropWaterDetailCardTitle">
          <img width={12} src={droplet} alt="droplet" />
          <span className="dropWaterDetailCardTitleValue"><AnimatedNumber value={memberInfo?.flowingWater || 0} /></span>/<span>200</span>
          <Tooltip title="最多可攒200滴，消耗后可继续领取" placement="right">
            <span className="dropWaterDetailCardTip">
              <Tips />
            </span>
          </Tooltip>
        </div>
        <div className="dropWaterDetailCardSub">
          <span className="dropWaterDetailCardSubLabel">免费水滴</span> 登录可领 50 永久水滴
        </div>
      </div>
      <div className="dropWaterDetailCard">
        <div className="dropWaterDetailCardTitle">
          <img width={12} src={droplet} alt="droplet" />
          <span className="dropWaterDetailCardTitleValue"><AnimatedNumber value={memberInfo?.memberDrops || 0} /></span>
          <Tooltip title="会员有效期内可用，升级获得更多水滴" placement="right">
            <span className="dropWaterDetailCardTip">
              <Tips />
            </span>
          </Tooltip>
        </div>
        <div className="dropWaterDetailCardSub">
          <span className="dropWaterDetailCardSubLabel">会员水滴</span>{' '}
          {currentExpireDate ? `${t('expiration_date')}${currentExpireDate}` : t('upgrade_to_get_more_drops')}
        </div>
      </div>
      <div className="dropWaterDetailCard">
        <div className="dropWaterDetailCardTitle">
          <img width={12} src={droplet} alt="droplet" />
          <span className="dropWaterDetailCardTitleValue"><AnimatedNumber value={memberInfo?.supplyDrop || 0} /></span>
          <Tooltip title="100天有效，每次获得新的补给水滴，剩余水滴有效期重置为100天" placement="right">
            <span className="dropWaterDetailCardTip">
              <Tips />
            </span>
          </Tooltip>
        </div>
        <div className="dropWaterDetailCardSub">
          <span className="dropWaterDetailCardSubLabel">补给水滴</span>{' '}
          {(supplyDropsExpiresAt && memberInfo?.supplyDrop) ? `${t('expiration_date')}${supplyDropsExpiresAt}` : ''}
        </div>
      </div>
      <div className="dropWaterDetailCard" style={{ display: memberInfo?.otherDrops ? 'block' : 'none' }}>
        <div className="dropWaterDetailCardTitle">
          <img width={12} src={droplet} alt="droplet" />
          <span className="dropWaterDetailCardTitleValue"><AnimatedNumber value={memberInfo?.otherDrops || 0} /></span>
        </div>
        <div className="dropWaterDetailCardSub">
          <span className="dropWaterDetailCardSubLabel">其他水滴</span>
        </div>
      </div>
    </div>
  );
}
